import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'components/styleguide/button/Button';
import IconButton from 'components/styleguide/icon_button/IconButton';

const styles = require('./Dialog.module.scss');

const FOCUSABLE_SELECTOR = 'button, [href]';

const randomId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 9)}`;

export type DialogContext = 'company' | 'talent' | 'neutral';

export interface DialogProps {
  cancelText?: string;
  cancelUrl?: string;
  confirmText?: string;
  confirmUrl?: string;
  context?: DialogContext;
  isOpen?: boolean;
  message: React.ReactNode;
  onClose?: () => void;
  onConfirm?: () => void;
  title: string;
  triggerId?: string;
}

export default function Dialog({
  cancelText = 'Cancel',
  cancelUrl,
  confirmText = 'Continue',
  confirmUrl,
  context = 'neutral',
  isOpen: isOpenProp = false,
  message,
  onClose,
  onConfirm,
  title,
  triggerId,
}: DialogProps) {
  const [isOpen, setIsOpen] = useState(isOpenProp);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [titleId] = useState(() => randomId('dialog-title'));
  const [messageId] = useState(() => randomId('dialog-message'));

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    onConfirm?.();
  }, [onConfirm]);

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleClose();
  }, [handleClose]);

  // Sync with controlled prop. Only mirrors the prop value forward; the
  // dialog can still be closed locally (X / Esc / backdrop) and the
  // parent does not have to update `isOpen` to react.
  useEffect(() => {
    setIsOpen(isOpenProp);
  }, [isOpenProp]);

  // Open from a non-React trigger element (Rails-rendered button etc.).
  useEffect(() => {
    if (!triggerId) return undefined;

    const trigger = document.getElementById(triggerId);
    if (!trigger) return undefined;

    const handleOpen = () => setIsOpen(true);
    trigger.addEventListener('click', handleOpen);
    return () => trigger.removeEventListener('click', handleOpen);
  }, [triggerId]);

  // Body scroll lock + focus trap + restore.
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => dialogRef.current?.focus());

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const renderMessage = () => {
    if (typeof message === 'string') {
      return <p id={messageId} className={styles.message}>{message}</p>;
    }
    return <div id={messageId} className={styles.message}>{message}</div>;
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className={styles.header}>
          <h3 id={titleId} className={styles.title}>{title}</h3>
          <IconButton
            type="terceary"
            size="S"
            context="neutral"
            iconName="x"
            onClick={handleClose}
            aria-label="Close dialog"
          />
        </div>

        <div className={styles.body}>
          {renderMessage()}
        </div>

        <div className={styles.footer}>
          <Button
            type="secondary"
            size="S"
            context={context}
            onClick={cancelUrl ? undefined : handleClose}
            href={cancelUrl}
          >
            {cancelText}
          </Button>
          <Button
            type="primary"
            size="S"
            context={context}
            onClick={confirmUrl ? undefined : handleConfirm}
            href={confirmUrl}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}
